import {
  useCallback,
  useMemo,
  createContext,
  useContext,
  useState,
  useLayoutEffect,
} from 'react'
import {
  RaceQuery,
  RaceUpdatedSubscription,
  useRaceQuery,
  useRaceUpdatedSubscription,
} from '../generated/graphql'
import type {
  ApolloError,
  SubscriptionResult,
  WatchQueryFetchPolicy,
} from '@apollo/client'

interface RaceContextI {
  id: null | string
  queryParams: Parameters<typeof useRaceQuery>[0]
  subscriptionParams: Parameters<typeof useRaceUpdatedSubscription>[0]

  /** @description merged race and subscription data */
  race: null | Omit<NonNullable<RaceQuery['race']>, '__typename'>
  previousRaceData: ReturnType<typeof useRaceQuery>['previousData']

  loading: boolean
  error?: ApolloError
  retry: () => void
}

const CurrentRaceContext = createContext<null | RaceContextI>(null)

export const CurrentRaceProvider = CurrentRaceContext.Provider

export const useCurrentRace = () => useContext(CurrentRaceContext)

export const useRaceFetcher = (
  id: string | null,
  skip = false,
  cachePolicy: WatchQueryFetchPolicy = 'cache-and-network'
): RaceContextI => {
  const [subscriptionData, setSubscriptionData] = useState<
    null | SubscriptionResult<RaceUpdatedSubscription>['data']
  >(null)

  const raceQueryParams = useMemo(
    (): Parameters<typeof useRaceQuery>[0] => ({
      fetchPolicy: cachePolicy,
      pollInterval: 120000,
      variables: {
        id,
        silksSize: 64,
      },
      skip: !id || skip,
    }),
    [id, skip, cachePolicy]
  )

  const raceSubscriptionUpdater = useCallback(
    ({
      subscriptionData,
    }: {
      subscriptionData: SubscriptionResult<RaceUpdatedSubscription>
    }) => setSubscriptionData(subscriptionData?.data),
    [setSubscriptionData]
  )

  const raceSubscriptionParams = useMemo(
    (): Parameters<typeof useRaceUpdatedSubscription>[0] => ({
      fetchPolicy: 'network-only' as const,
      variables: {
        raceId: id,
      },
      skip: !id || skip,
      onSubscriptionData: raceSubscriptionUpdater,
    }),
    [id, skip, raceSubscriptionUpdater]
  )

  const raceFetch = useRaceQuery(raceQueryParams)

  useRaceUpdatedSubscription(raceSubscriptionParams)

  const retry = useCallback(() => void raceFetch.refetch(), [raceFetch])

  const race = raceFetch?.data?.race

  useLayoutEffect(() => {
    setSubscriptionData(null)
  }, [raceQueryParams?.variables?.id, id, skip])

  const raceMerged = useMemo(() => {
    if (!race) return null
    if (race.id !== subscriptionData?.raceUpdated?.id) return race
    const mergedCompetitors: typeof race.competitors = race.competitors?.map(
      (competitorOriginal) => {
        const updatedCompetitor =
          subscriptionData?.raceUpdated?.competitors?.find(
            (c) => c.tabNo === competitorOriginal.tabNo
          )

        return {
          ...competitorOriginal,
          scratched:
            updatedCompetitor?.scratched || competitorOriginal.scratched,
          __typename: undefined,
        }
      }
    )

    return {
      ...race,
      ...((race &&
        subscriptionData?.raceUpdated?.id === race.id &&
        subscriptionData?.raceUpdated) ||
        null),
      competitors: mergedCompetitors,
      __typename: undefined,
    }
  }, [race, subscriptionData?.raceUpdated])

  return {
    id,
    retry,
    queryParams: raceQueryParams,
    subscriptionParams: raceSubscriptionParams,
    race: raceMerged,
    loading: raceFetch?.loading,
    previousRaceData: raceFetch?.previousData,
  }
}

export function useRace(
  id: string | null = null,
  skip = false,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-first'
) {
  const currentRace = useCurrentRace()

  const isCurrentRace = currentRace?.id === id

  const fetchedRace = useRaceFetcher(
    id,
    !id || isCurrentRace || skip,
    fetchPolicy
  )

  return isCurrentRace ? currentRace : fetchedRace
}

